<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="search-view has-side-bars">
    <div v-if="dataSearch" class="results-wrapper">
      <div class="lateral-filters">
        <GrowUpMenu
          v-if="isMobile"
          :mobile-filters-open="mobileFiltersOpen"
          :clean-filters="cleanFilters"
          :title-menu="$t('filter_by')"
          @toggle-mobile-filter="toggleMobileFilter"
        >
          <template v-slot:menu-options>
            <LateralFilters
              :active-filter="activeFilter"
              :search-time-limits="searchTimeLimits"
              @lateral-filter-pressed="lateralFilterChange"
              @toggle-filter="toggleFilter"
            />
          </template>
        </GrowUpMenu>
        <LateralFilters
          v-else
          :active-filter="activeFilter"
          :search-time-limits="searchTimeLimits"
          @lateral-filter-pressed="lateralFilterChange"
          @toggle-filter="toggleFilter"
        />
      </div>
      <div class="main-container">
        <div class="search-input-container">
          <div class="fav-title-wrapper">
            <h3>{{ $t('fav_list') }}</h3>
            <SearchInput @search-pressed="searchHandler" @toggle-data-loading="toggleDataLoading" />
          </div>
          <div v-if="isMobile" class="button-filter-wrapper" @click="() => toggleMobileFilter()">
            <div class="icon-filter" />
          </div>
          <div v-if="dataLoading" class="icon-loader" />
        </div>
        <TypeFilter @lateral-filter-change="lateralFilterChange" />
        <div class="fav-results-wrapper">
          <div class="search-results-container">
            <div class="search-results-text">{{ dataSearch.count }} {{ $t('favs_for') }} <b>{{ searching }}</b></div>
            <FilterSort type="fav-list" @toggle-data-loading="toggleDataLoading" />
          </div>
          <SearchSelectedFilters @lateral-filter-pressed="lateralFilterChange" @toggle-filter="toggleFilter" />
        </div>
        <div class="notify-title">
          <span>{{ $t('update_noti') }}</span>
        </div>
        <SearchList
          :data-search="dataSearch"
          type="fav-list"
          @reload="reloadData"
        />
        <PaginationSearch :data-search="dataSearch" :items-for-page="itemsForPage" @toggle-data-loading="toggleDataLoading" />
      </div>
    </div>
    <Loader v-else :text="$t('loading')" />
  </div>
</template>

<script>
import GrowUpMenu from '../mobile/GrowUpMenu.vue'
import FilterSort from '../search/FilterSort.vue'
import LateralFilters from '../search/LateralFilters.vue'
import PaginationSearch from '../search/PaginationSearch.vue'
import SearchSelectedFilters from '../search/SearchSelectedFilters.vue'
import SearchInput from '../SearchInput.vue'
import SearchList from '../search/SearchList.vue'
import Loader from '../Loader.vue'
import TypeFilter from '../search/TypeFilter.vue'
import dayjs from 'dayjs'
import device from '@/utils/device'
import { getUserFavs } from '@/services/user-api'

/**
 * The Favourites list page of Portal with params and query params of search in the url
 * @displayName Search
 * @example [none]
 */
export default {
  components: {
    GrowUpMenu,
    FilterSort,
    LateralFilters,
    PaginationSearch,
    SearchSelectedFilters,
    SearchInput,
    SearchList,
    Loader,
    TypeFilter,
  },
  props: {},
  data() {
    return {
      activeFilter: '',
      searching: '',
      itemsForPage: 10,
      dataLoading: false,
      mobileFiltersOpen: false,
      reloadTimes: 0,
    }
  },
  asyncComputed: {
    dataSearch() {
      this.searching = this.$route.params.params
      let computedStringThesaurus = null
      if(this.$route.query.theme) {
        computedStringThesaurus = this.$route.query.theme
        if (this.$route.query.subtheme) {
          computedStringThesaurus += ',' + this.$route.query.subtheme
          if(this.$route.query.detail) {
            computedStringThesaurus += ',' + this.$route.query.detail
          }
        }
      }
      this.reloadTimes
      return getUserFavs(
        this.$store.state.i18n.lang,
        'LIST',
        this.sortByFilter,
        (this.$route.query.page ? parseInt(this.$route.query.page) - 1 : 0) * this.itemsForPage,
        this.itemsForPage,
        computedStringThesaurus,
        this.searching ? this.searching : '',
        this.$route.query.source,
        this.$route.query.territory,
        this.$route.query.publishDateRange
      ).then(resp => {
        if (resp.code === 500 || resp.code === 404 || resp.code === 401) {
          this.$store.dispatch('deleteUser').then(() => this.$router.push({ name: 'Cover' }))
        }
        return resp
      })
    },
  },
  computed: {
    getDevice() {
      return device
    },
    searchOptions() {
      return this.dataSearch
    },
    searchTimeLimits() {
      if (this.dataSearch.publishDateInit && this.dataSearch.publishDateEnd) {
        let timeLimits = {
          min: dayjs(this.dataSearch.publishDateInit.split('T')[0] + 'T00:00:00Z'),
          max: dayjs(this.dataSearch.publishDateEnd.split('T')[0] + 'T00:00:00Z'),
        }
        return timeLimits
      } else return null
    },
    queryLateralFilters() {
      let filterObj = []
      let arrayWords = []
      if (this.$route.query.theme) {
        arrayWords = this.$route.query.theme.split(',')
        arrayWords.forEach(el =>
          filterObj.push({
            value: el,
            type: 'theme',
          })
        )
      }
      if (this.$route.query.subtheme) {
        arrayWords = this.$route.query.subtheme.split(',')
        arrayWords.forEach(el =>
          filterObj.push({
            value: el,
            type: 'subtheme',
          })
        )
      }
      if (this.$route.query.detail) {
        arrayWords = this.$route.query.detail.split(',')
        arrayWords.forEach(el =>
          filterObj.push({
            value: el,
            type: 'detail',
          })
        )
      }
      return filterObj
    },
    sortByFilter() {
      return this.$route.query.sortBy ? this.$route.query.sortBy : 'relevance'
    },
  },
  watch: {
    // whenever search was loaded, this function will run
    searchOptions() {
      this.toggleDataLoading(false)
    },
  },
  methods: {
    reloadData () {
      this.reloadTimes += 1
    },
    /**
     * Clean all selected filters
     * @public This is a public method
     */
    cleanFilters() {
      this.$router.push({
        name: 'Profile',
        params: { params: this.$route.params.params, optype: this.$route.params.optype },
        query: {},
      })
    },
    /**
     * Toggle mobile filter
     * @public This is a public method
     * @param {Boolean} val value that wants to set
     */
    toggleMobileFilter(val) {
      this.mobileFiltersOpen = val ? val : !this.mobileFiltersOpen
    },
    /**
     * Toggle one of the lateral filters avaliable
     * @public This is a public method
     * @param {String} filter Id of selected filter to toggle
     */
    toggleFilter(filter) {
      this.activeFilter = this.activeFilter === filter ? '' : filter
    },
    /**
     * Toggle loading to on/off
     * @public This is a public method
     * @param {String} val not required value that wants to set
     */
    toggleDataLoading: function (val) {
      this.dataLoading = val !== undefined ? val : !this.dataLoading
    },
    /**
     * Detect when the user search a new value and update the url
     * @public This is a public method
     * @param {String} value search string value
     */
    searchHandler: function (value) {
      let defaultParams = { optype: this.$route.params.optype }
      this.activeFilter = ''
      if (value !== '') defaultParams = { params: value, optype: this.$route.params.optype }
      if (this.$route.params.params !== value) {
        if (value === '' && !this.$route.params.params) this.toggleDataLoading()
        let queryPartial = {}
        if (this.$route.query) {
          queryPartial = { ...this.$route.query }
          delete queryPartial.page
          delete queryPartial.publishDateRange
        }
        this.$router.push({ name: 'Profile', params: defaultParams, query: queryPartial })

      } else this.toggleDataLoading()
    },
    /**
     * Update query params with current lateral filter information
     * @param {String} filtVal Value of selected filter
     * @param {String} filtType Type of filter to change (theme, subtheme, detail, source, territory)
     * @param {Boolean} recursive to know if function has recursive calls
     * @public This is a public method
     */
    lateralFilterChange(filtVal, filtType, multiselect, recursive) {
      if (!recursive) this.toggleDataLoading()
      let arrayFilters = []
      if (this.$route.query[filtType]) {
        arrayFilters = this.$route.query[filtType].split(',')
        if (typeof arrayFilters === String) arrayFilters = [arrayFilters]
        if (filtType === 'theme') {
          this.queryLateralFilters.forEach(el => {
            if (el.type === 'subtheme')
              this.lateralFilterChange(el.value, el.type, multiselect, true)
          })
        }
        if (filtType === 'subtheme') {
          this.queryLateralFilters.forEach(el => {
            if (el.type === 'detail') this.lateralFilterChange(el.value, el.type, multiselect, true)
          })
        }
        if (arrayFilters.find(el => el === filtVal)) {
          arrayFilters.splice(arrayFilters.indexOf(filtVal), 1)
        } else {
          if (!multiselect) arrayFilters = [filtVal]
          else {
            if (filtVal === '') arrayFilters = []
            else arrayFilters.push(filtVal)
          }
        }
      } else arrayFilters.push(filtVal)
      arrayFilters = arrayFilters.join(',')
      if (arrayFilters !== '') {
        let queryUpdate = { ...this.$route.query }
        //reset pagination on filter
        if (queryUpdate.page) delete queryUpdate.page
        if (queryUpdate.publishDateRange) delete queryUpdate.publishDateRange
        if (filtType === 'theme') {
          delete queryUpdate.subtheme
          delete queryUpdate.detail
        } else if (filtType === 'subtheme') delete queryUpdate.detail
        if (filtType !== 'publishDateRange' || arrayFilters.indexOf('Invalid Date') === -1) {
          queryUpdate[filtType] = arrayFilters
          this.$router.push({
            name: 'Profile',
            params: { params: this.$route.params.params, optype: this.$route.params.optype },
            query: {
              ...queryUpdate,
            },
          })
        }
      } else {
        let queryUpdate = { ...this.$route.query }
        delete queryUpdate[filtType]
        this.$router.push({
          name: 'Profile',
          params: { params: this.$route.params.params, optype: this.$route.params.optype },
          query: {
            ...queryUpdate,
          },
        })
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
.search-view
  position: relative
  flex: 1 1 0%
  // overflow: hidden
  padding-top: vw(40px)
  padding-bottom: vw(130px)

  .mobile &
    padding-bottom: vw(40px)

.results-wrapper
  display: flex
  min-height: "calc(100vh - %s - %s - %s)" % ($header-height $subheader-height $footer-height)
  gap: vw(60px)

.search-input-container
  display: flex
  align-items: center
  margin-bottom: vw(30px)

  .mobile &
    margin-bottom: vw(20px)
    gap: vw(15px)

  .fav-title-wrapper
    display: flex
    justify-content: space-between
    align-items: center
    width: 100%

    h3
      m-font-size(24, 28)
      font-weight: 400
      margin-block-start: 0
      margin-block-end: 0

  .button-filter-wrapper
    padding: vw(10px)
    border: 1px solid black
    border-radius: 50%

    .icon-filter
      m-icon("filter", 20, $black)

.lateral-filters
  flex: 0 0 vw(300px)
  width: vw(300px)

  .mobile &
    position: fixed
    bottom: 0
    left: 0
    z-index: 8
    padding-top: 0
    width: 100%
    transition: transform 0.25s ease-in-out

  &.menu-grow
    transform: translateY(0)

.main-container
  display: flex
  flex: 1 1 0%
  flex-direction: column
  max-width: 100%

  >>> .search-input-wrapper
    width: 50%

    .mobile &
      width: 100%

.fav-results-wrapper
  margin-bottom: vw(20px)
  padding-bottom: vw(35px)
  border-bottom: 1px solid $grey-grey

  .mobile &
    margin-bottom: vw(10px)

.notify-title
  display: flex
  justify-content: flex-end
  margin-bottom: vw(20px)
  width: 100%

  span
    m-font-size(14, 20)
    color: $grey-dark
    font-weight: 400

.search-results-container
  display: flex
  justify-content: space-between
  align-items: center
  margin-bottom: vw(20px)

  .mobile &
    margin-bottom: vw(10px)

.search-results-text
  m-font-size(14, 20)
  color: #000

.icon-loader
  m-icon("loader", 20)
  margin-left: vw(20px)
  animation: rotate 1s infinite linear

@keyframes rotate
  from
    transform: rotate(0)

  to
    transform: rotate(360deg)
</style>
